import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { createRentalMachine, updateRentalMachine, getRentalMachines } from '../services/apiService';
import ImageUploader from '../components/ImageUploader';
import GalleryUploader from '../components/GalleryUploader';

const RentalMachineForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    type: 'CO2',
    power: '',
    workArea: '',
    image: '',
    gallery: [],
    description: '',
    features: '',
    availableAtWorkshop: false,
    availableOnSite: false,
    shortTerm: false,
    longTerm: false,
    status: 'available',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      fetchMachine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  const fetchMachine = async () => {
    try {
      const machines = await getRentalMachines();
      const machine = machines.find((m) => m.id === id);
      if (machine) {
        setFormData({
          ...machine,
          features: machine.features?.join('\n') || '',
          gallery: Array.isArray(machine.gallery) ? machine.gallery : [],
        });
      }
    } catch (err) {
      console.error('Error fetching rental machine:', err);
      setError('Erreur lors du chargement');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = {
        ...formData,
        features: formData.features ? formData.features.split('\n').filter((f) => f.trim()) : [],
        gallery: Array.isArray(formData.gallery) ? formData.gallery : [],
      };

      if (isEdit) {
        await updateRentalMachine(id, data);
      } else {
        await createRentalMachine(data);
      }

      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Error saving:', err);
      setError("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="rental-machine-form">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au dashboard
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
            {isEdit ? 'Modifier la machine en location' : 'Ajouter une machine en location'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Nom *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="rental-name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="CO2">CO2</option>
                  <option value="Fibre">Fibre</option>
                  <option value="Mixte">Mixte</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Puissance *</label>
                <input
                  type="text"
                  name="power"
                  value={formData.power}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="100W"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Zone de travail *</label>
                <input
                  type="text"
                  name="workArea"
                  value={formData.workArea}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="1000 x 600 mm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Statut *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="available">Disponible</option>
                  <option value="rented">Louée</option>
                </select>
              </div>
            </div>

            <div>
              <ImageUploader
                label="Image principale"
                required
                value={formData.image}
                onChange={(v) => setFormData((prev) => ({ ...prev, image: v }))}
              />
            </div>

            <div>
              <GalleryUploader
                label="Galerie d'images"
                value={formData.gallery}
                onChange={(arr) => setFormData((prev) => ({ ...prev, gallery: arr }))}
                maxImages={12}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Équipement inclus (une ligne par élément)</label>
              <textarea
                name="features"
                value={formData.features}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                placeholder="Machine révisée&#10;Formation d'une heure&#10;Assistance téléphonique"
              />
            </div>

            {/* Options de location */}
            <div className="border rounded-lg p-6 bg-gray-50">
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#5C3A21' }}>
                Formules proposées pour cette machine
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: 'availableAtWorkshop', label: 'Sur place (dans notre atelier)' },
                  { name: 'availableOnSite', label: 'Sur site (chez le client)' },
                  { name: 'shortTerm', label: 'Court terme' },
                  { name: 'longTerm', label: 'Long terme' },
                ].map((opt) => (
                  <label
                    key={opt.name}
                    className="flex items-center space-x-3 p-3 bg-white rounded-lg border cursor-pointer hover:bg-amber-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      name={opt.name}
                      checked={!!formData[opt.name]}
                      onChange={handleChange}
                      className="w-5 h-5 rounded"
                      data-testid={`rental-opt-${opt.name}`}
                    />
                    <span className="text-sm font-medium text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Cochez les options réellement disponibles pour cette machine. Les tarifs restent toujours sur devis.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#5C3A21' }}
                data-testid="rental-submit"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RentalMachineForm;
